import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getRouteApi, Link, useNavigate, useRouter } from '@tanstack/react-router';
import { LogIn } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { isApiError } from '../../api/client';
import { authApi } from '../../api/endpoints';
import { errorMessage, fieldErrors, issueMessage, issuesOf, requestIdOf } from '../../api/errors';
import { queryKeys } from '../../api/queries';
import { useI18n } from '../../i18n/I18nProvider';
import { Button } from '../../ui/Button';
import { Callout } from '../../ui/Feedback';
import { Code, useDocumentTitle } from '../../ui/Layout';
import { TextField } from '../../ui/TextField';
import { AuthLayout, authStyles as styles } from './AuthLayout';

const route = getRouteApi('/login');

export function LoginPage() {
  const { t } = useI18n();
  useDocumentTitle(t.auth.signInTitle);
  const search = route.useSearch();
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const login = useMutation({
    mutationFn: () => authApi.login({ email: email.trim(), password }),
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: ['org'] });
      await queryClient.invalidateQueries({ queryKey: queryKeys.me });
      // search.redirect was validated as a same-app path (safeRedirect) by the route.
      if (search.redirect) router.history.push(search.redirect);
      else await navigate({ to: '/' });
    },
  });

  const allIssues = issuesOf(login.error);
  const issues = fieldErrors(t, allIssues);
  const otherIssues = allIssues.filter((issue) => !['email', 'password'].includes(issue.path));
  const failure = login.error
    ? isApiError(login.error) && login.error.status === 401
      ? t.auth.invalidCredentials
      : errorMessage(t, login.error)
    : null;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    login.mutate();
  };

  return (
    <AuthLayout>
      <form className={styles.form} onSubmit={onSubmit} noValidate aria-labelledby="login-title">
        <div className={styles.formHeader}>
          <h1 id="login-title" className={styles.formTitle}>
            {t.auth.signInTitle}
          </h1>
          <p className={styles.formSubtitle}>{t.auth.signInSubtitle}</p>
        </div>
        {search.reason === 'expired' && !login.error ? (
          <Callout tone="info" role="status">
            {t.auth.sessionExpired}
          </Callout>
        ) : null}
        {failure && (allIssues.length === 0 || otherIssues.length > 0) ? (
          <Callout tone="danger" role="alert" title={failure}>
            {otherIssues.map((issue) => (
              <p key={`${issue.path}:${issue.issue}`}>{issueMessage(t, issue)}</p>
            ))}
            {requestIdOf(login.error) &&
            !(isApiError(login.error) && login.error.status === 401) ? (
              <span>
                {t.errors.reference}: <Code>{requestIdOf(login.error)}</Code>
              </span>
            ) : null}
          </Callout>
        ) : null}
        <TextField
          label={t.auth.email}
          type="email"
          autoComplete="username"
          dir="ltr"
          value={email}
          onChange={setEmail}
          errorMessage={issues.email}
          isRequired
          autoFocus
        />
        <TextField
          label={t.auth.password}
          type="password"
          autoComplete="current-password"
          dir="ltr"
          value={password}
          onChange={setPassword}
          errorMessage={issues.password}
          isRequired
        />
        <Button
          type="submit"
          variant="primary"
          className={styles.submit}
          isPending={login.isPending}
          icon={<LogIn size={16} className="flip-rtl" aria-hidden="true" />}
        >
          {t.auth.signIn}
        </Button>
        <p className={styles.switch}>
          {t.auth.noAccount} <Link to="/register">{t.auth.createAccount}</Link>
        </p>
      </form>
    </AuthLayout>
  );
}
